import { Drawer, Group, Stack, TextInput, Title } from "@mantine/core"
import { useForm } from "@mantine/form"
import { Button, CountrySelector } from "@tfg/ui"
import { AxiosError } from "axios"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import {
  prodSiteSchema,
  prodSiteValidationType,
} from "@tfg/types/schemas/productionSites"

import { useEditProductionSiteMutation } from "~/pages/SupplierPortal/ProductionSites/hooks/useEditProductionSiteMutation.ts"
import { COUNTRIES } from "~/utils/countries.ts"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  productionSite: SupplierResponses.ProductionSite
  onClose: () => void
}

export const EditProductionSiteDrawer = ({
  productionSite,
  onClose,
}: Props) => {
  const { t } = useTranslation(["supplier"])

  const { mutate, isPending: isLoading } = useEditProductionSiteMutation(
    productionSite.id,
    {
      onSuccess: () => {
        handleClose()
        showSuccessNotification()
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: "Error",
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
      },
    }
  )

  const form = useForm<prodSiteValidationType>({
    validate: zod4Resolver(prodSiteSchema),
    initialValues: {
      name: productionSite.name,
      address: productionSite.address,
      countryIso3166: productionSite.country,
    },
  })

  const onSubmit = (values: prodSiteValidationType) => {
    mutate({
      request: values,
    })
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Drawer
      opened
      position="right"
      onClose={handleClose}
      title={<Title order={3}>{t("productionSiteModal.editTitle")}</Title>}
      size="xl"
    >
      <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
        <Stack gap="xl">
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.information")}
            </Title>
            <TextInput
              {...form.getInputProps("name")}
              label={t("productionSiteModal.labels.name")}
            />
            <TextInput
              {...form.getInputProps("address")}
              label={t("productionSiteModal.labels.address")}
            />
            <CountrySelector
              onChange={form.getInputProps("countryIso3166").onChange}
              value={form.getInputProps("countryIso3166").value}
              label={t("productionSiteModal.labels.country")}
              searchable
              countriesMapper={COUNTRIES}
            />
          </Stack>

          <Group justify="right">
            <Button variant="tertiary" onClick={handleClose}>
              {t("productionSiteModal.actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("productionSiteModal.actions.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
